import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Box,
  CardContent,
  Toolbar,
  IconButton,
  Badge,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  ListItemButton,
  ListItemText,
  List,
  ListSubheader,
  Card,
  CardHeader,
  ListItem,
  CardActions,
  ListItemAvatar,
  Avatar,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";

import { Link, useLocation, useHistory } from "react-router-dom";

import { connect } from "react-redux";
import NoRecordMessage from "../../common/components/NoRecordMessage";

import { approveTraining } from "../../../../store/cpd/actions";

import { getSchoolSubLevelSubjects } from "../../../../store/posts/actions";

import ConfirmationDialog from "../../common/components/ConfirmationDialog";
import ReactPaginate from "react-paginate";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useRequiredAuth } from "../../common/guard/requiredAuth";
import axios from "axios";
import { showError, showSuccess } from "../../../toastify";

import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { isEmpty } from "lodash";
import PreviewPdfUrl from "../../common/components/PreviewPdfUrl";
import TraineesFormDialog from "./TraineesFormDialog";
import AttendanceDialog from "./AttendanceDialog";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
};

const formatDateRange = (start, end) => {
  const s = formatDate(start);
  const e = formatDate(end);
  if (s && e) return `${s} - ${e}`;
  if (s) return `${s} -`;
  if (e) return `- ${e}`;
  return "";
};

const TrainingTraineesDialog = (props) => {
  const {
    loading,
    user,
    approveTraining,
    showDialog,
    setShowDialog,
    training,
  } = props;

  const { hasPermissions } = useRequiredAuth();
  const canIssueCertificates = hasPermissions(["CPD_TRAININGS_ISSUE_CERTIFICATES"]);

  const [trainingCohorts, setTrainingCohorts] = useState([]);
  const [issuedTraineeIds, setIssuedTraineeIds] = useState(() => new Set());
  const [loadingIssued, setLoadingIssued] = useState(false);

  const getTrainingCohorts = async (trainingId) => {
    try {
      const { data } = await axios.get(
        `/api/cpd/training-cohorts/${trainingId}`,
      );

      setTrainingCohorts(data);
    } catch (error) { }
  };

  useEffect(() => {
    getTrainingCohorts(training.id);
  }, [training]);

  const fetchIssuedCertificates = async () => {
    if (!training?.id) return;
    setLoadingIssued(true);
    try {
      const { data } = await axios.get(`/api/cpd/trainings/${training.id}/certificates`);
      const ids = new Set(
        (Array.isArray(data) ? data : [])
          .map((c) => c?.traineeId)
          .filter((v) => v != null && String(v).trim() !== "")
          .map((v) => String(v).trim()),
      );
      setIssuedTraineeIds(ids);
    } catch (e) {
      // If not allowed or not configured yet, keep empty; don't block modal.
      setIssuedTraineeIds(new Set());
    } finally {
      setLoadingIssued(false);
    }
  };

  useEffect(() => {
    if (!showDialog) return;
    if (canIssueCertificates) {
      fetchIssuedCertificates();
    } else {
      setIssuedTraineeIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog, training?.id, canIssueCertificates]);

  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);

  const onClose = () => setShowDialog(false);

  const headerDateRange = useMemo(() => {
    const trainingRange = formatDateRange(training?.startDate, training?.endDate);
    if (trainingRange) return trainingRange;

    const firstCohort = Array.isArray(trainingCohorts) ? trainingCohorts[0] : null;
    return formatDateRange(firstCohort?.startDate, firstCohort?.endDate);
  }, [training?.startDate, training?.endDate, trainingCohorts]);

  const visibleCohorts = useMemo(() => {
    const list = Array.isArray(trainingCohorts) ? trainingCohorts : [];
    // When server-side scoping removes venues (e.g. district users),
    // avoid rendering empty cohort containers.
    return list.filter((c) => (c?.venues || []).length > 0);
  }, [trainingCohorts]);

  return (
    <Dialog onClose={onClose} open={showDialog} fullWidth maxWidth="xl">
      <DialogTitle className="text-uppercase">
        <span className="text-primary ">Training Trainees</span> | {training.title}
        {!!headerDateRange && (
          <span className="text-muted text-capitalize" style={{ fontSize: "0.85rem" }}>
            {" "}
            ({headerDateRange})
          </span>
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            minHeight: "69vh",
          }}
        >
          <div className="row">
            <div className="col-12">
              {visibleCohorts.length === 0 && (
                <NoRecordMessage message="No venues available for your scope." />
              )}

              {visibleCohorts.map((cohort, cohortIndex) => (
                <fieldset
                  key={cohortIndex}
                  className={`row mx-0 border rounded elevated mb-3`}
                >
                  <legend
                    className={`px-3 w-auto text-dark`}
                    style={{
                      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      fontWeight: 400,
                      fontSize: "0.8rem",
                      lineHeight: "1em",
                      letterSpacing: "0.00938em",
                    }}
                  >
                    Cohort #{cohortIndex + 1}
                    {!!formatDateRange(cohort?.startDate, cohort?.endDate) && (
                      <span
                        className="text-muted text-capitalize"
                        style={{ marginLeft: 8, fontSize: "0.8rem" }}
                      >
                        ({formatDateRange(cohort?.startDate, cohort?.endDate)})
                      </span>
                    )}
                  </legend>

                  <div className="col-12 px-3">
                    <fieldset
                      className={`px-3 mx-0 form-group border rounded elevated bg-white`}
                    >
                      <legend
                        className={` w-auto text-primary`}
                        style={{
                          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                          fontWeight: 400,
                          fontSize: "0.82rem",
                          lineHeight: "1em",
                          letterSpacing: "0.00938em",
                        }}
                      >
                        [{cohort.venues?.length}] Venue
                        {cohort.venues?.length > 1 ? "s" : ""}
                      </legend>

                      {cohort.venues?.map((cohortVenue) => (
                        <CohortVenueCard
                          key={cohortVenue.id}
                          cohortVenue={cohortVenue}
                          training={training}
                          cohort={cohort}
                          canIssueCertificates={canIssueCertificates}
                          issuedTraineeIds={issuedTraineeIds}
                          setIssuedTraineeIds={setIssuedTraineeIds}
                          loadingIssued={loadingIssued}
                          getTrainingCohorts={getTrainingCohorts}
                          onMarkAttendance={() => {
                            setSelectedVenue(cohortVenue);
                            setSelectedCohort(cohort);
                            setShowAttendanceDialog(true);
                          }}
                        />
                      ))}
                    </fieldset>
                  </div>
                </fieldset>
              ))}

              {showAttendanceDialog && (
                <AttendanceDialog
                  showDialog={showAttendanceDialog}
                  setShowDialog={setShowAttendanceDialog}
                  cohortVenue={selectedVenue}
                  cohort={selectedCohort}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions className="d-flex py-2 justify-content-center"></DialogActions>
    </Dialog>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return {
    user,
    loading,
  };
};

export default connect(mapStateToProps, {
  approveTraining,
  getSchoolSubLevelSubjects,
})(TrainingTraineesDialog);

const CohortVenueCard = (props) => {
  const {
    training,
    cohort,
    cohortVenue,
    getTrainingCohorts,
    onMarkAttendance,
    canIssueCertificates,
    issuedTraineeIds,
    setIssuedTraineeIds,
    loadingIssued,
  } = props;

  const [showTraineeFormDialog, setShowTraineeFormDialog] = useState(false);
  const [issuingIds, setIssuingIds] = useState(() => new Set());
  const [issuingAll, setIssuingAll] = useState(false);

  const { hasPermissions } = useRequiredAuth();

  const isIssued = useCallback(
    (traineeId) => issuedTraineeIds?.has?.(String(traineeId)),
    [issuedTraineeIds],
  );

  const issueOne = async (trainee) => {
    const traineeId = String(trainee?.id || "");
    if (!traineeId || !training?.id) return;
    if (isIssued(traineeId)) return;

    setIssuingIds((prev) => new Set(prev).add(traineeId));
    try {
      const { data } = await axios.post(
        `/api/cpd/trainings/${training.id}/certificates/issue`,
        { traineeId },
      );
      if (data?.issued) {
        setIssuedTraineeIds((prev) => {
          const next = new Set(prev);
          next.add(traineeId);
          return next;
        });
        showSuccess("Certificate issued");
      } else if (data?.eligible === false) {
        showError(data?.reason || "Not eligible for certificate");
      } else {
        // Mark as issued only if server returned an existing certificate.
        if (data?.certificate) {
          setIssuedTraineeIds((prev) => {
            const next = new Set(prev);
            next.add(traineeId);
            return next;
          });
        }
      }
    } catch (e) {
      showError(e);
    } finally {
      setIssuingIds((prev) => {
        const next = new Set(prev);
        next.delete(traineeId);
        return next;
      });
    }
  };

  const issueAllForVenue = async () => {
    const trainees = Array.isArray(cohortVenue?.trainees) ? cohortVenue.trainees : [];
    const pending = trainees
      .map((t) => String(t?.id || ""))
      .filter((id) => id && !isIssued(id));
    if (!pending.length) {
      showSuccess("All certificates already issued");
      return;
    }
    setIssuingAll(true);
    try {
      let issued = 0;
      let alreadyIssued = 0;
      let notEligible = 0;

      for (const id of pending) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(`/api/cpd/trainings/${training.id}/certificates/issue`, {
          traineeId: id,
        });

        if (data?.issued) {
          issued += 1;
          setIssuedTraineeIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        } else if (data?.certificate) {
          alreadyIssued += 1;
          setIssuedTraineeIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        } else if (data?.eligible === false) {
          notEligible += 1;
        }
      }

      showSuccess(
        `Certificates: ${issued} issued${alreadyIssued ? `, ${alreadyIssued} already issued` : ""}${notEligible ? `, ${notEligible} not eligible` : ""}.`,
      );
    } catch (e) {
      showError(e);
    } finally {
      setIssuingAll(false);
    }
  };

  return (
    <>
      <Card className=" mb-3 border">
        <CardContent>
          <Typography variant="h5" component="div">
            {cohortVenue.venue?.sector?.district?.name || "All"}
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 1.5 }}>
            {cohortVenue.venue?.sector?.name} sector / {cohortVenue.venue?.name}
            {hasPermissions(["CPD_TRAININGS_ASSIGN_TRAINEES"]) && (
              <Button
                disabled={
                  cohortVenue?.trainees?.length >= +cohortVenue.numOfTrainees
                }
                size="small"
                className="ml-2"
                variant="contained"
                startIcon={<span className="material-icons">add</span>}
                onClick={() => setShowTraineeFormDialog(true)}
              >
                Add Trainees
              </Button>
            )}

            {hasPermissions(["CPD_TRAININGS_ATTENDANCE"]) && (
              <Button
                size="small"
                className="ml-2"
                variant="outlined"
                color="info"
                startIcon={<span className="material-icons">event_available</span>}
                onClick={onMarkAttendance}
                disabled={!cohortVenue?.trainees?.length}
              >
                Attendance
              </Button>
            )}

            {canIssueCertificates &&
              String(training?.id || "").trim() !== "" &&
              String(cohortVenue?.trainees?.length || 0) !== "0" &&
              String(cohortVenue?.trainees?.length || 0) !== "" && (
                <Button
                  size="small"
                  className="ml-2"
                  variant="outlined"
                  color="success"
                  startIcon={<span className="material-icons">verified</span>}
                  onClick={issueAllForVenue}
                  disabled={
                    issuingAll || loadingIssued || !cohortVenue?.trainees?.length
                  }
                >
                  {issuingAll ? "Issuing..." : "Issue certificates"}
                </Button>
              )}
          </Typography>

          <fieldset className={`px-3 mt-2 form-group`}>
            <legend
              className={` w-auto text-primary`}
              style={{
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                fontWeight: 400,
                fontSize: "0.82rem",
                lineHeight: "1em",
                letterSpacing: "0.00938em",
              }}
            >
              ({cohortVenue?.trainees?.length || 0}/{cohortVenue.numOfTrainees})
              Trainees
            </legend>

            {!!cohortVenue?.trainees?.length && (
              <table className="table table-striped">
                <thead>
                  <tr style={{ backgroundColor: "#f0f2f5" }}>
                    <th scope="col">#</th>
                    <th scope="col">EmployeeId</th>
                    <th scope="col">Names</th>
                    <th scope="col">NID Number</th>
                    <th scope="col">Position</th>
                    <th scope="col">Role</th>
                    {canIssueCertificates && (
                      <th scope="col" style={{ width: 160 }}>
                        Certificate
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {cohortVenue?.trainees?.map((item, trainerIndex) => (
                    <tr key={item.id}>
                      <td className="text-left">{trainerIndex + 1}</td>
                      <td className="text-left">
                        <strong>{item.employeeId}</strong>
                      </td>
                      <td className="text-left">
                        {item.lastName} {item.firstName}
                      </td>
                      <td className="text-left">{item.nidNumber}</td>
                      <td className="text-left">{item.positionName}</td>
                      <td className="text-left">{item.role}</td>
                      {canIssueCertificates && (
                        <td className="text-left">
                          {isIssued(item.id) ? (
                            <span className="badge badge-success">Issued</span>
                          ) : (
                            <>
                              <span className="badge badge-secondary mr-2">
                                Not issued
                              </span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                disabled={
                                  loadingIssued ||
                                  issuingIds.has(String(item.id))
                                }
                                onClick={() => issueOne(item)}
                              >
                                {issuingIds.has(String(item.id))
                                  ? "Issuing..."
                                  : "Issue"}
                              </Button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!cohortVenue?.trainees?.length && (
              <div className="container text-center jumbotron py-3">
                <p className="lead">Trainees not set</p>
              </div>
            )}
          </fieldset>
        </CardContent>
      </Card>

      {showTraineeFormDialog && (
        <TraineesFormDialog
          showDialog={showTraineeFormDialog}
          setShowDialog={setShowTraineeFormDialog}
          cohortVenue={cohortVenue}
          training={training}
          currentTrainees={cohortVenue.trainees.map(({ id }) => id)}
          getTrainingCohorts={getTrainingCohorts}
        />
      )}
    </>
  );
};
